import { PDF417Error, PDF417Symbol, SingleBitPerPixelBitmap, pdf417 } from "../printing/barcodes/pdf417";
import generatePDF, { PDFObject, PDFObjectType, PDFPage } from "./pdf-generation";
import { code128B } from "../printing/barcodes/code128";
import { Address } from "../UserContext";
import { logRocketError } from "@/utils/logUtils";

export interface Barcode2DItem {
  fnsku: string;
  quantity: number; // cannot be <= 0.
  expiration?: Date;
}

export const fbaBoxLabel2DBarcode = (fbaShipmentId: string, items: Barcode2DItem[]): [PDF417Error, PDF417Symbol] => {
  let stringData: string = `AMZN,PO:${fbaShipmentId}`;
  items.forEach(item => {
    stringData += `,FNSKU:${item.fnsku},QTY:${item.quantity}`;

    if (item.expiration) {
      // @NOTE the file 'FBA_2DBarcode_Requirements_en-US.pdf', which comes from Amazon seller central,
      // dated 2016, says the date format MUST be YYMMDD, with a key/code: 'EXP'. This is different from
      // FNSKU expirations which are MM/DD/YYYY.
      const YY: string = item.expiration.getFullYear().toString().substring(2);
      const MM: string = item.expiration.getMonth().toString().padStart(2, "0");
      const DD: string = item.expiration.getDate().toString().padStart(2, "0");
      stringData += `,EXP:${YY}${MM}${DD}`;
    }
  });

  return pdf417(stringData);
}

export function mockBase64BoxLabelPdf() {
  return base64BoxLabelsPdf(
    {
      name: "Aunt May's",
      address1: "36 Fuller Place",
      state: "NY",
      city: "Brooklyn",
      zipcode: "11215",
      country: "US",
    },
    {
      name: "Amazon Distribution Center",
      company: "Amazon.com Services, Inc.",
      address1: "401 Independence Road",
      city: "Florence",
      state: "NJ",
      zipcode: "08518-2200",
      country: "US",
    },
    "FBA17SM82ZS6",
    "My Shipment Name",
    "CLT2",
    new Date(),
    [
      {
        boxNumber: 1,
        boxContents: [
          {
            fnsku: "X0033A9F6Z",
            quantity: 25,
          }
        ]
      }
    ]
  );
}

function baseLabelObjects(
  fromAddress: Address,
  toAddress: Address,
  shipmentId: string,
  shipmentName: string,
  shipmentDateCreated: Date,
  boxNumber?: number, // if this is for an FBA box label, the box number is concatenated with the shipment id (omitted for pallet labels though)
): PDFObject[] {
  const truncate = (s: string, limit: number) => {
    return s.length <= limit ? s : s.slice(0, limit-3) + "...";
  }

  const addressToString = (prefix: string, address: Address): [boolean, string] => {
    if (!address || !address.address1 || !address.name || !address.city || !address.state || !address.zipcode) {
      const message = `Address provided when generating box or pallet labels is invalid! (missing line1, name, city, state, zipcode, or country code) ${JSON.stringify(address)}`;
      logRocketError(message);
      return [ false, "" ];
    }
    
    // @TODO validate length of every address line. if we can just paste in a Helvetica width table in here we can make it easier.

    let str = `${prefix}${address.name}\n`

    if (address.company) {
      str += `${address.company}\n`;
    }

    if (address.address1) {
      str += `${address.address1}\n`;
    }
    if (address.address2) {
      str += `${address.address2}\n`;
    }

    str += `${address.city} ${address.state} ${address.zipcode}
${address.country ?? "US"}`;

    return [ true, str ];
  }

  let fullShipmentId = shipmentId;
  if (boxNumber) {
    fullShipmentId += `U${`${boxNumber}`.padStart(6, "0")}`;
  }
  const shipmentIdCode128: SingleBitPerPixelBitmap = code128B(fullShipmentId);
  const [barcode2DError, shipmentId2DBarcode] = pdf417(fullShipmentId);

  let [success, addressText] = addressToString("SHIP FROM:\n", fromAddress);
  if (!success) {
    return [];
  }
  const shipFromText: string = addressText;

  [success, addressText] = addressToString("SHIP TO:\n", toAddress);
  if (!success) {
    return [];
  }
  const shipToText: string = addressText;

  const shipmentIdRowText = `${truncate(shipmentName, 29).padEnd(29, " ")}Created: ${shipmentDateCreated.toLocaleString()}`;  

  const objects: PDFObject[] = [
    {
      type: PDFObjectType.text,
      left: 34,
      bottom: 418,
      text: "PLEASE LEAVE THIS LABEL UNCOVERED"
    },
    {
      type: PDFObjectType.text,
      left: 20,
      bottom: 400,
      text: "FBA"
    },
    {
      type: PDFObjectType.rect,
      left: 10,
      bottom: 396,
      width: 286,
      height: 3
    },
    {
      type: PDFObjectType.text,
      left: 12,
      bottom: 385,
      fontSize: 8,
      text: shipFromText
    },
    {
      type: PDFObjectType.text,
      left: 146,
      bottom: 385,
      fontSize: 8,
      text: shipToText
    },

    {
      type: PDFObjectType.rect,
      left: 10,
      bottom: 320,
      width: 286,
      height: 12,
    },
    {
      type: PDFObjectType.text,
      left: 12,
      bottom: 323,
      fontSize: 8,
      color: [ 1, 1, 1 ],
      text: shipmentIdRowText
    },
    {
      type: PDFObjectType.singleBitPerPixelImage,
      bitmap: shipmentIdCode128,
      left: 10,
      bottom: 270,
      width: 200,
      height: 35
    },
    {
      type: PDFObjectType.text,
      left: 40,
      bottom: 255,
      text: fullShipmentId
    },
    {
      type: PDFObjectType.singleBitPerPixelImage,
      bitmap: shipmentId2DBarcode.bitmap,
      left: 290,
      bottom: 170,
      width: 70,
      height: 140,
      rotateRadians: Math.PI/2
    },
    {
      type: PDFObjectType.text,
      left: 34,
      bottom: 5,
      text: "PLEASE LEAVE THIS LABEL UNCOVERED"
    },
  ];

  return objects;
}
export const default4x6PDFDimensions = { width: 306, height: 432 };

export function base64PalletLabelPdf(
  fromAddress: Address,
  toAddress: Address,
  shipmentId: string,
  shipmentName: string,
  shipmentDateCreated: Date,
  copies: number = 1
) {
  const objects = baseLabelObjects(fromAddress, toAddress, shipmentId, shipmentName, shipmentDateCreated);
  const pages: PDFPage[] = [];
  for (let i = 0; i < copies; i++) {
    pages.push({ objects });
  }
  const pdf = generatePDF(306, 432, pages);
  return window.btoa(pdf);
}

export function base64BoxLabelsPdf(
  fromAddress: Address,
  toAddress: Address,
  shipmentId: string,
  shipmentName: string,
  fulfillmentCentreId: string,
  shipmentDateCreated: Date,
  boxes: {
    boxNumber: number,
    boxContents?: Barcode2DItem[], // if boxContentsMode === 'barcode2D', this should be provided
  }[]
): string {
  const pdfPages: PDFPage[] = [];

  for (let i = 0; i < boxes.length; i++) {
    let totalUnits = 0;
    let boxContentsBarcode: PDF417Symbol | null;
    let boxContentsBarcodeError;
    if (boxes[i].boxContents?.length > 0) {
      totalUnits = boxes[i]?.boxContents.reduce((acc, curr) => acc + curr.quantity, 0) ?? 0;
      [boxContentsBarcodeError, boxContentsBarcode] = fbaBoxLabel2DBarcode(shipmentId, boxes[i].boxContents);
    }
  
    const objects = baseLabelObjects(fromAddress, toAddress, shipmentId, shipmentName, shipmentDateCreated, boxes[i].boxNumber);
  
    objects.push(
      {
        type: PDFObjectType.text,
        left: 100,
        bottom: 212,
        text: `BOX ${boxes[i].boxNumber}`,
      },
      {
        type: PDFObjectType.rect,
        left: 10,
        bottom: 208,
        width: 200,
        height: 2,
      },
    );
  
    if (boxContentsBarcode) {
      objects.push(
        { 
          type: PDFObjectType.singleBitPerPixelImage,
          bitmap: boxContentsBarcode.bitmap,
          left: 15,
          bottom: 40,
          width: 286,
          height: 120,
        },
        {
          type: PDFObjectType.text,
          left: 55,
          bottom: 24,
          fontSize: 8,
          text: `Dest: ${fulfillmentCentreId}, Shipment: ${shipmentId}, Units: ${totalUnits}`
        },
      );
    }

    pdfPages.push({ objects });
  }

  const pdf = generatePDF(default4x6PDFDimensions.width, default4x6PDFDimensions.height, pdfPages);

  return window.btoa(pdf);
}
